import { z } from 'zod';
import { 
  regions, locations, campings, nationalParks,
  insertRegionSchema, insertLocationSchema, insertCampingSchema, insertUserSchema
} from './schema';

export const errorSchemas = {
  validation: z.object({ message: z.string(), field: z.string().optional() }),
  notFound: z.object({ message: z.string() }),
  internal: z.object({ message: z.string() }),
  unauthorized: z.object({ message: z.string() }),
};

export const api = {
  auth: {
    login: {
      method: 'POST' as const,
      path: '/api/auth/login' as const,
      input: z.object({ email: z.string().email(), password: z.string() }),
      responses: {
        200: z.object({ id: z.number(), email: z.string(), role: z.string(), token: z.string() }),
        401: errorSchemas.unauthorized,
      },
    },
    register: {
      method: 'POST' as const,
      path: '/api/auth/register' as const,
      // Minimum 10-char password: balances brute-force resistance with usability.
      // Login does NOT enforce a min so legacy users with shorter passwords (set
      // before this rule existed) can still sign in; they'll be required to
      // upgrade only when they next change their password.
      input: z.object({
        email: z.string().email(),
        password: z.string().min(10, 'Password must be at least 10 characters').max(200),
      }),
      responses: {
        201: z.object({ id: z.number(), email: z.string(), role: z.string(), token: z.string() }),
        400: errorSchemas.validation,
      },
    }
  },
  regions: {
    list: {
      method: 'GET' as const,
      path: '/api/regions' as const,
      responses: {
        200: z.array(z.custom<typeof regions.$inferSelect>()),
      },
    },
    getBySlug: {
      method: 'GET' as const,
      path: '/api/regions/:slug' as const,
      responses: {
        200: z.custom<typeof regions.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    }
  },
  locations: {
    list: {
      method: 'GET' as const,
      path: '/api/locations' as const,
      input: z.object({ 
        regionId: z.number().optional(),
        category: z.string().optional()
      }).optional(),
      responses: {
        200: z.array(z.custom<typeof locations.$inferSelect>()),
      },
    },
    getBySlug: {
      method: 'GET' as const,
      path: '/api/locations/:slug' as const,
      responses: {
        200: z.custom<typeof locations.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    compare: {
      method: 'GET' as const,
      path: '/api/locations/compare' as const,
      input: z.object({ slugs: z.string() }), // comma separated
      responses: {
        200: z.array(z.custom<typeof locations.$inferSelect>()),
        400: errorSchemas.validation,
      },
    }
  },
  campings: {
    list: {
      method: 'GET' as const,
      path: '/api/campings' as const,
      input: z.object({ regionId: z.number().optional() }).optional(),
      responses: {
        200: z.array(z.custom<typeof campings.$inferSelect>()),
      }
    }
  },
  nationalParks: {
    list: {
      method: 'GET' as const,
      path: '/api/national-parks' as const,
      responses: {
        200: z.array(z.custom<typeof nationalParks.$inferSelect>()),
      }
    },
    getBySlug: {
      method: 'GET' as const,
      path: '/api/national-parks/:slug' as const,
      responses: {
        200: z.custom<typeof nationalParks.$inferSelect>(),
        404: errorSchemas.notFound,
      }
    }
  }
};

export function buildUrl(path: string, params?: Record<string, string | number>): string {
  let url = path;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (url.includes(`:${key}`)) {
        url = url.replace(`:${key}`, String(value));
      }
    });
  }
  return url;
}

export type RegionResponse = z.infer<typeof api.regions.getBySlug.responses[200]>;
export type LocationResponse = z.infer<typeof api.locations.getBySlug.responses[200]>;
export type CampingResponse = z.infer<typeof api.campings.list.responses[200]>[number];
